import { ArrowLeft, ArrowRight } from '@jsluna/icons';
import { childMatchesType, filterProps } from '@jsluna/utils';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

function _extends() {
  _extends = Object.assign || function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];

      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }

    return target;
  };

  return _extends.apply(this, arguments);
}

function _inheritsLoose(subClass, superClass) {
  subClass.prototype = Object.create(superClass.prototype);
  subClass.prototype.constructor = subClass;
  subClass.__proto__ = superClass;
}

function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;

  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }

  return target;
}

function _assertThisInitialized(self) {
  if (self === void 0) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }

  return self;
}

var _excluded = ["element", "active", "children", "className", "onClick", "onFocus"];

var TabLink = function TabLink(_ref) {
  var element = _ref.element,
      active = _ref.active,
      children = _ref.children,
      className = _ref.className,
      onClick = _ref.onClick,
      onFocus = _ref.onFocus,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);

  var tabEl = element;
  var props = Object.assign({}, rest, {
    className: classnames('ln-c-tabs__link', className, {
      'is-active': active
    }),
    role: 'tab',
    'aria-selected': active,
    onClick: onClick,
    onFocus: onFocus
  });
  return /*#__PURE__*/React.isValidElement(tabEl) ? /*#__PURE__*/React.cloneElement(tabEl, props, children) : /*#__PURE__*/React.createElement(tabEl, props, children);
};

TabLink.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  active: PropTypes.bool,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onClick: PropTypes.func,
  onFocus: PropTypes.func
};
TabLink.defaultProps = {
  element: 'button',
  active: false,
  className: undefined,
  onClick: undefined,
  onFocus: undefined
};
TabLink.displayName = 'TabLink';
var _excluded$1 = ["className", "children", "type", "color", "showPrevArrow", "showNextArrow", "animateDistance", "getChildren", "onArrowClick", "onTabLinkFocus"];

var TabList = function TabList(_ref) {
  var className = _ref.className,
      children = _ref.children,
      type = _ref.type,
      color = _ref.color,
      showPrevArrow = _ref.showPrevArrow,
      showNextArrow = _ref.showNextArrow,
      animateDistance = _ref.animateDistance,
      getChildren = _ref.getChildren,
      onArrowClick = _ref.onArrowClick,
      onTabLinkFocus = _ref.onTabLinkFocus,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);

  var isResponsive = type !== 'fill' && React.Children.count(children) > 1 && onArrowClick;
  var transform = isResponsive && animateDistance ? "translateX(" + animateDistance + "%)" : undefined;

  if (getChildren) {
    getChildren(children);
  }

  return /*#__PURE__*/React.createElement("nav", _extends({}, filterProps(rest, ['visibleTabs']), {
    className: classnames('ln-c-tabs', className, type && "ln-c-tabs--" + type, color && "ln-c-tabs--" + color, {
      'has-prev-arrow': showPrevArrow,
      'has-next-arrow': showNextArrow
    })
  }), /*#__PURE__*/React.createElement("div", {
    className: "ln-c-tabs__list-wrap"
  }, /*#__PURE__*/React.createElement("div", {
    className: "ln-c-tabs__list",
    role: "tablist",
    style: {
      transform: transform
    }
  }, isResponsive ? React.Children.map(children, function (child, index) {
    var props = {
      key: index
    };

    if (childMatchesType(child, TabLink)) {
      props.onFocus = onTabLinkFocus;
    }

    return /*#__PURE__*/React.cloneElement(child, props);
  }) : children), isResponsive && [/*#__PURE__*/React.createElement("button", {
    type: "button",
    key: "arrowPrev",
    className: "ln-c-tabs__link ln-c-tabs__link--arrow ln-c-tabs__link--arrow-prev",
    tabIndex: "-1",
    onClick: function onClick() {
      return onArrowClick(false);
    },
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(ArrowLeft, null)), /*#__PURE__*/React.createElement("button", {
    type: "button",
    key: "arrowNext",
    className: "ln-c-tabs__link ln-c-tabs__link--arrow ln-c-tabs__link--arrow-next",
    tabIndex: "-1",
    onClick: function onClick() {
      return onArrowClick(true);
    },
    "aria-hidden": true
  }, /*#__PURE__*/React.createElement(ArrowRight, null))]));
};

TabList.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,

  /** Use different TabLink style */
  type: PropTypes.oneOf(['pill', 'fill', 'deep']),

  /** Use a different colour theme variant */
  color: PropTypes.oneOf(['light', 'dark']),
  showPrevArrow: PropTypes.bool,
  showNextArrow: PropTypes.bool,
  animateDistance: PropTypes.number,
  getChildren: PropTypes.func,
  onArrowClick: PropTypes.func,
  onTabLinkFocus: PropTypes.func
};
TabList.defaultProps = {
  className: undefined,
  type: undefined,
  color: undefined,
  showPrevArrow: false,
  showNextArrow: false,
  animateDistance: 0,
  getChildren: undefined,
  onArrowClick: undefined,
  onTabLinkFocus: undefined
};
TabList.displayName = 'TabList';

var getAnimateDistance = function getAnimateDistance(selectedPageIndex, remainingTabs, tabsPerPage) {
  if (selectedPageIndex === undefined) {
    return 0;
  }

  var animateDistance = selectedPageIndex * -100;

  if (remainingTabs < 0) {
    animateDistance += remainingTabs * -1 * (100 / tabsPerPage);
  }

  return animateDistance;
};

var getElementDimensions = function getElementDimensions(element) {
  if (!element.getBoundingClientRect) {
    return {};
  }

  return element.getBoundingClientRect();
};

var _excluded$2 = ["element"];
var ARROW_WIDTH = 48;
var VISIBLE_TABS = 2;

var Tabs = /*#__PURE__*/function (_Component) {
  _inheritsLoose(Tabs, _Component);

  function Tabs(props) {
    var _this;

    _this = _Component.call(this, props) || this;
    _this.state = {
      animateDistance: 0,
      selectedIndex: 0,
      numberOfTabs: 0,
      remainingTabs: 0
    };
    _this.handleArrowClick = _this.handleArrowClick.bind(_assertThisInitialized(_this));
    _this.handleTabLinkFocus = _this.handleTabLinkFocus.bind(_assertThisInitialized(_this));
    _this.isResponsiveEnabled = _this.isResponsiveEnabled.bind(_assertThisInitialized(_this));
    _this.setSelectedTab = _this.setSelectedTab.bind(_assertThisInitialized(_this));
    _this.getNumberOfTabs = _this.getNumberOfTabs.bind(_assertThisInitialized(_this));
    _this.getNumberOfRemainingTabs = _this.getNumberOfRemainingTabs.bind(_assertThisInitialized(_this));
    return _this;
  }

  var _proto = Tabs.prototype;

  _proto.getNumberOfTabs = function getNumberOfTabs(children) {
    var _this2 = this;

    var numberOfTabs = this.state.numberOfTabs;
    var tabs = React.Children.count(children);

    if (tabs !== numberOfTabs) {
      this.setState({
        numberOfTabs: tabs
      }, function () {
        _this2.setState({
          remainingTabs: _this2.getNumberOfRemainingTabs()
        });
      });
    }
  };

  _proto.getNumberOfRemainingTabs = function getNumberOfRemainingTabs() {
    var _this$state = this.state,
        numberOfTabs = _this$state.numberOfTabs,
        selectedIndex = _this$state.selectedIndex;
    return numberOfTabs - (selectedIndex + 1) * VISIBLE_TABS;
  };

  _proto.setSelectedTab = function setSelectedTab(selectedIndex) {
    var _this3 = this;

    this.setState({
      selectedIndex: selectedIndex
    }, function () {
      var remainingTabs = _this3.getNumberOfRemainingTabs();

      _this3.setState({
        remainingTabs: remainingTabs,
        animateDistance: getAnimateDistance(selectedIndex, remainingTabs, VISIBLE_TABS)
      });
    });
  };

  _proto.isResponsiveEnabled = function isResponsiveEnabled() {
    var numberOfTabs = this.state.numberOfTabs;
    var type = this.props.type;
    return type !== 'fill' && numberOfTabs > VISIBLE_TABS;
  };

  _proto.handleArrowClick = function handleArrowClick(next) {
    var selectedIndex = this.state.selectedIndex;
    var modifier = next ? 1 : -1;
    this.setSelectedTab(selectedIndex + modifier);
  };

  _proto.handleTabLinkFocus = function handleTabLinkFocus(e) {
    var selectedIndex = this.state.selectedIndex;

    if (this.isResponsiveEnabled()) {
      var _getElementDimensions = getElementDimensions(this.wrapper),
          wrapperX = _getElementDimensions.x,
          wrapperWidth = _getElementDimensions.width;

      var _getElementDimensions2 = getElementDimensions(e.target),
          focussedX = _getElementDimensions2.x,
          focussedWidth = _getElementDimensions2.width;

      if (focussedX + focussedWidth >= wrapperX + wrapperWidth) {
        this.setSelectedTab(selectedIndex + 1);
      } else if (focussedX + focussedWidth <= wrapperX + ARROW_WIDTH) {
        this.setSelectedTab(selectedIndex - 1);
      }
    }
  };

  _proto.render = function render() {
    var _this4 = this;

    var _this$props = this.props,
        element = _this$props.element,
        rest = _objectWithoutPropertiesLoose(_this$props, _excluded$2);

    var Element = element;
    var _this$state2 = this.state,
        selectedIndex = _this$state2.selectedIndex,
        remainingTabs = _this$state2.remainingTabs,
        animateDistance = _this$state2.animateDistance;
    var responsiveProps = {};

    if (this.isResponsiveEnabled()) {
      responsiveProps = {
        showNextArrow: remainingTabs > 0,
        showPrevArrow: selectedIndex > 0,
        animateDistance: animateDistance
      };
    }

    return /*#__PURE__*/React.createElement(Element, {
      ref: function ref(wrapper) {
        _this4.wrapper = wrapper;
      }
    }, /*#__PURE__*/React.createElement(TabList, _extends({
      onArrowClick: this.handleArrowClick,
      onTabLinkFocus: this.handleTabLinkFocus,
      getChildren: this.getNumberOfTabs
    }, responsiveProps, rest)));
  };

  return Tabs;
}(Component);

Tabs.propTypes = {
  element: PropTypes.oneOfType([PropTypes.element, PropTypes.func, PropTypes.string]),
  children: PropTypes.node.isRequired,
  type: PropTypes.oneOf(['pill', 'fill', 'deep'])
};
Tabs.defaultProps = {
  element: 'div',
  type: undefined
};
Tabs.displayName = 'Tabs';
var _excluded$3 = ["element", "children", "className", "active"];

var TabPanel = function TabPanel(_ref) {
  var element = _ref.element,
      children = _ref.children,
      className = _ref.className,
      active = _ref.active,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded$3);

  var Element = element;
  return /*#__PURE__*/React.createElement(Element, _extends({}, rest, {
    role: "tabpanel",
    "aria-hidden": !active,
    className: classnames(className, !active && 'ln-u-hidden')
  }), children);
};

TabPanel.propTypes = {
  element: PropTypes.oneOfType([PropTypes.string, PropTypes.func, PropTypes.object]),
  children: PropTypes.node,
  className: PropTypes.string,
  active: PropTypes.bool
};
TabPanel.defaultProps = {
  element: 'div',
  children: undefined,
  className: undefined,
  active: true
};
TabPanel.displayName = 'TabPanel';
export { Tabs, TabList, TabLink, TabPanel };